import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import PhotoLightbox from '../components/PhotoLightbox';
import HelpButton from '../components/HelpButton';
import { compressImage } from '../utils/imageCompression';

function currentDefaultYear() {
  const now = new Date();
  return now.getFullYear() + (now.getMonth() >= 9 ? 1 : 0);
}

export default function MyLetterPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [year, setYear] = useState(currentDefaultYear());
  const [letter, setLetter] = useState(null);
  const [freeText, setFreeText] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingText, setSavingText] = useState(false);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const textSaveTimeout = useRef(null);

  useEffect(() => {
    if (user?.id) loadMyLetter();
  }, [year, user]);

  async function loadMyLetter() {
    setLoading(true);
    setError('');
    try {
      const data = await api.getLetter(user.id, year);
      setLetter(data);
      setFreeText(data.free_text || '');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function handleTextChange(value) {
    setFreeText(value);
    if (textSaveTimeout.current) clearTimeout(textSaveTimeout.current);
    textSaveTimeout.current = setTimeout(async () => {
      setSavingText(true);
      try {
        await api.updateMyFreeText(year, value);
      } catch (err) {
        setError(err.message);
      } finally {
        setSavingText(false);
      }
    }, 800);
  }

  async function handleDeleteItem(itemId) {
    if (!window.confirm('¿Eliminar este regalo de tu lista?')) return;
    try {
      await api.deleteMyItem(itemId);
      loadMyLetter();
    } catch (err) {
      setError(err.message);
    }
  }

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>Cargando tu carta... 🎄</div>;
  }

  return (
    <div style={styles.page}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <button className="btn btn-ghost" onClick={() => navigate('/home')}>← Volver</button>
        <HelpButton title="✉️ Mi Carta">
          <p>Aquí escribes tu carta a los Reyes Magos para el año que elijas.</p>
          <p>La parte de <strong>texto libre</strong> es para contar lo que quieras: cómo te has portado, peticiones generales, etc.</p>
          <p>La <strong>lista de regalos</strong> es para pedir cosas concretas, una a una, con foto si quieres (puedes hacer una foto nueva, elegir de tu galería, o reutilizar una de tus "Inspiraciones").</p>
          <p>👀 <strong>Importante:</strong> tú nunca verás si alguien ya ha "reservado" comprarte alguno de tus regalos — así no se estropea la sorpresa. El resto de la familia sí lo ve, para no duplicar compras.</p>
        </HelpButton>
      </div>

      <div style={styles.yearSelector}>
        <button className="btn btn-ghost" onClick={() => setYear(y => y - 1)}>←</button>
        <h2 style={styles.year}>✉️ Mi carta {year}</h2>
        <button className="btn btn-ghost" onClick={() => setYear(y => y + 1)}>→</button>
      </div>

      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <label className="label">Querido Rey Mago...</label>
        <textarea
          className="input"
          style={styles.textarea}
          value={freeText}
          onChange={e => handleTextChange(e.target.value)}
          placeholder="Escribe aquí lo que te gustaría, cómo te has portado este año, o cualquier cosa que quieras contar..."
          rows={6}
        />
        {savingText && <p style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: '0.3rem' }}>Guardando...</p>}
      </div>

      <div style={styles.itemsHeader}>
        <h3 style={{ color: 'var(--gold)', fontFamily: 'var(--font-display)' }}>🎁 Mi lista de regalos</h3>
        <button className="btn btn-primary" onClick={() => setShowAddForm(true)}>+ Añadir</button>
      </div>

      <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginBottom: '1rem' }}>
        Tú nunca verás si alguien ha reservado un regalo de tu lista — así no se estropea la sorpresa.
      </p>

      <div style={styles.itemsList}>
        {letter?.items?.length === 0 && (
          <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '1rem' }}>
            Todavía no has añadido ningún regalo a tu lista.
          </p>
        )}
        {letter?.items?.map(item => (
          <div key={item.id} className="card" style={styles.itemCard}>
            {item.photo_url && (
              <img
                src={item.photo_url}
                alt=""
                style={{ ...styles.itemPhoto, cursor: 'pointer' }}
                onClick={() => setLightboxSrc(item.photo_url)}
              />
            )}
            <div style={styles.itemInfo}>
              <p style={styles.itemDesc}>{item.description}</p>
            </div>
            <button className="btn btn-danger" style={styles.deleteBtn} onClick={() => handleDeleteItem(item.id)}>✕</button>
          </div>
        ))}
      </div>

      {error && <p className="error-msg" style={{ textAlign: 'center', marginTop: '1rem' }}>⚠ {error}</p>}

      {showAddForm && (
        <AddItemModal
          year={year}
          onClose={() => setShowAddForm(false)}
          onAdded={() => { setShowAddForm(false); loadMyLetter(); }}
        />
      )}

      <PhotoLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />
    </div>
  );
}

function AddItemModal({ year, onClose, onAdded }) {
  const [description, setDescription] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [existingPhotoUrl, setExistingPhotoUrl] = useState(null);
  const [pickFromInspirations, setPickFromInspirations] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  async function handleFileSelected(e) {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const compressed = await compressImage(file);
      setPhotoFile(compressed);
      setPhotoPreview(URL.createObjectURL(compressed));
      setPickFromInspirations(false);
    } catch (err) {
      setError('No se pudo procesar la foto, inténtalo de nuevo');
    }
  }

  function handlePickInspiration(insp) {
    setPhotoFile(null); // ya está en el servidor, no hay que volver a subirla
    setExistingPhotoUrl(insp.photo_url);
    setPhotoPreview(insp.photo_url);
    setPickFromInspirations(false);
  }

  async function handleSave() {
    if (!description.trim()) {
      setError('Escribe una descripción del regalo');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await api.addMyItem(year, description.trim(), photoFile, existingPhotoUrl);
      onAdded();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  function clearPhoto() {
    setPhotoFile(null);
    setExistingPhotoUrl(null);
    setPhotoPreview(null);
  }

  return (
    <div style={styles.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <h3 style={{ fontFamily: 'var(--font-display)', color: 'var(--gold)' }}>Añadir regalo</h3>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={styles.modalBody}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label className="label">¿Qué te gustaría?</label>
            <textarea
              className="input"
              rows={3}
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Ej: Unas zapatillas de running talla 42, marca Asics"
            />
          </div>

          <div>
            <label className="label">Foto (opcional)</label>
            {photoPreview ? (
              <div style={styles.previewWrap}>
                <img src={photoPreview} alt="" style={styles.preview} />
                <button className="btn btn-ghost" onClick={clearPhoto}>
                  Quitar foto
                </button>
              </div>
            ) : (
              <div style={styles.photoButtons}>
                <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => cameraInputRef.current?.click()}>
                  📷 Cámara
                </button>
                <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => fileInputRef.current?.click()}>
                  🖼️ Galería
                </button>
                <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setPickFromInspirations(true)}>
                  ✨ Inspiración
                </button>
              </div>
            )}
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: 'none' }}
              onChange={handleFileSelected}
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFileSelected}
            />
          </div>

          {error && <p className="error-msg">⚠ {error}</p>}
        </div>

        <div style={styles.modalFooter}>
          <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Guardando...' : 'Añadir a mi lista'}
          </button>
        </div>
      </div>

      {pickFromInspirations && (
        <InspirationPickerModal
          onClose={() => setPickFromInspirations(false)}
          onPick={handlePickInspiration}
        />
      )}
    </div>
  );
}

function InspirationPickerModal({ onClose, onPick }) {
  const { user } = useAuth();
  const [inspirations, setInspirations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.listInspirations(user?.id)
      .then(setInspirations)
      .finally(() => setLoading(false));
  }, [user]);

  return (
    <div style={styles.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <h3 style={{ fontFamily: 'var(--font-display)', color: 'var(--gold)' }}>Tus inspiraciones</h3>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={styles.modalBody}>
          {loading ? (
            <p style={{ color: 'var(--text-muted)', textAlign: 'center' }}>Cargando...</p>
          ) : inspirations.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', textAlign: 'center' }}>
              Todavía no has guardado ninguna foto de inspiración.
            </p>
          ) : (
            <div style={styles.inspirationGrid}>
              {inspirations.map(insp => (
                <img
                  key={insp.id}
                  src={insp.photo_url}
                  alt=""
                  style={styles.inspirationThumb}
                  onClick={() => onPick(insp)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: { padding: '1rem' },
  yearSelector: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: '1rem', marginBottom: '1.5rem',
  },
  year: { color: 'var(--gold)', fontSize: '1.2rem', minWidth: '180px', textAlign: 'center' },
  textarea: { resize: 'vertical', minHeight: '120px', fontFamily: 'var(--font-body)', lineHeight: 1.6 },
  itemsHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: '0.5rem',
  },
  itemsList: { display: 'flex', flexDirection: 'column', gap: '0.75rem' },
  itemCard: { display: 'flex', alignItems: 'center', gap: '0.75rem' },
  itemPhoto: { width: '56px', height: '56px', borderRadius: '8px', objectFit: 'cover', flexShrink: 0 },
  itemInfo: { flex: 1 },
  itemDesc: { fontSize: '0.92rem', lineHeight: 1.4 },
  deleteBtn: { padding: '0.4rem 0.7rem', flexShrink: 0 },
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
    display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000,
  },
  modal: {
    background: 'var(--bg-card)', border: '1px solid var(--border)',
    borderRadius: '16px 16px 0 0', width: '100%', maxWidth: '500px',
    maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
  },
  modalHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--border)',
  },
  closeBtn: { background: 'none', color: 'var(--text-muted)', fontSize: '1.2rem', cursor: 'pointer' },
  modalBody: { padding: '1.5rem', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '1.25rem', flex: 1 },
  modalFooter: {
    padding: '1rem 1.5rem', borderTop: '1px solid var(--border)',
    display: 'flex', justifyContent: 'flex-end', gap: '0.75rem',
  },
  photoButtons: { display: 'flex', gap: '0.5rem' },
  previewWrap: { display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'center' },
  preview: { width: '100%', maxHeight: '200px', objectFit: 'cover', borderRadius: '8px' },
  inspirationGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem',
  },
  inspirationThumb: {
    width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: '8px',
    cursor: 'pointer', border: '2px solid var(--border)',
  },
};
